import { createRouter, createWebHistory } from 'vue-router'
import HomeView from '../views/HomeView.vue'
import { getUserInfo, getAccessToken } from '@/services/auth'
import { PERMISSIONS, ROLE_PERMISSIONS } from '../utils/permission'

const routes = [
  {
    path: '/',
    name: 'home',
    component: HomeView,
    meta: {
      requiresAuth: true
    }
  },
  {
    path: '/login',
    name: 'login',
    component: () => import('../views/LoginView.vue'),
    meta: {
      requiresAuth: false,
      hiddenFromNav: true
    }
  },
  {
    path: '/register',
    name: 'register',
    component: () => import('../views/RegisterView.vue'),
    meta: {
      requiresAuth: false,
      hiddenFromNav: true
    }
  },
  {
    path: '/change-password',
    name: 'change-password',
    component: () => import('../views/ChangePasswordView.vue'),
    meta: {
      requiresAuth: true,
      hiddenFromNav: true
    }
  },
  {
    path: '/password-unlock',
    name: 'password-unlock',
    component: () => import('../views/PasswordUnlockView.vue'),
    meta: {
      requiresAuth: false,
      hiddenFromNav: true
    }
  },
  {
    path: '/change-password-by-old',
    name: 'change-password-by-old',
    component: () => import('../views/ChangePasswordByOldView.vue'),
    meta: {
      requiresAuth: false,
      hiddenFromNav: true
    }
  },
  {
    path: '/courses',
    name: 'courses',
    component: () => import('../views/CoursesView.vue'),
    meta: {
      requiresAuth: true,
      requiresPermission: PERMISSIONS.VIEW_COURSES
    }
  },
  {
    path: '/documents',
    name: 'documents',
    component: () => import('../views/DocumentsView.vue'),
    meta: {
      requiresAuth: true,
      requiresPermission: PERMISSIONS.VIEW_DOCUMENTS
    }
  },
  {
    path: '/course-management',
    name: 'course-management',
    component: () => import('../views/CourseManagementView.vue'),
    meta: {
      requiresAuth: true,
      requiresRole: ['teacher', 'admin']
    }
  },
  {
    path: '/question-bank',
    name: 'question-bank',
    component: () => import('../views/QuestionBankView.vue'),
    meta: {
      requiresAuth: true,
      requiresRole: ['teacher', 'admin']
    }
  },
  {
    path: '/public-questions',
    name: 'public-questions',
    component: () => import('../views/StudentQuestionBankView.vue'),
    meta: {
      requiresAuth: true
    }
  },
  {
    path: '/exam-papers',
    name: 'exam-papers',
    component: () => import('../views/ExamPaperView.vue'),
    meta: {
      requiresAuth: true,
      requiresRole: ['teacher', 'admin']
    }
  },
  {
    path: '/grading',
    name: 'grading',
    component: () => import('../views/GradingView.vue'),
    meta: {
      requiresAuth: true,
      requiresRole: ['teacher', 'admin']
    }
  },
  {
    path: '/exams',
    name: 'exams',
    component: () => import('../views/ExamsView.vue'),
    meta: {
      requiresAuth: true,
      requiresPermission: PERMISSIONS.VIEW_EXAMS
    }
  },
  {
    path: '/chatbot',
    name: 'chatbot',
    component: () => import('../views/ChatbotView.vue'),
    meta: {
      requiresAuth: true,
      requiresPermission: PERMISSIONS.USE_CHATBOT
    }
  },
  {
    path: '/profile',
    name: 'profile',
    component: () => import('../views/ProfileView.vue'),
    meta: {
      requiresAuth: true,
      requiresPermission: PERMISSIONS.MANAGE_OWN_PROFILE
    }
  },
  {
    path: '/users',
    name: 'users',
    component: () => import('../views/UserManagementView.vue'),
    meta: {
      requiresAuth: true,
      requiresPermission: PERMISSIONS.VIEW_USERS
    }
  },
  {
    path: '/admin/work-guidance',
    name: 'work-guidance-admin',
    component: () => import('../views/WorkGuidanceAdminView.vue'),
    meta: {
      requiresAuth: true,
      requiresRole: ['admin'],
      hiddenFromNav: false
    }
  },
  {
    path: '/admin/announcement',
    name: 'announcement-admin',
    component: () => import('../views/AnnouncementAdminView.vue'),
    meta: {
      requiresAuth: true,
      requiresRole: ['admin']
    }
  },
  {
    path: '/case-library',
    name: 'case-library',
    component: () => import('../views/CaseLibraryView.vue'),
    meta: { requiresAuth: true }
  },
  {
    path: '/case-library/manage',
    name: 'case-library-manage',
    component: () => import('../views/CaseLibraryAdminView.vue'),
    meta: {
      requiresAuth: true,
      requiresRole: ['teacher', 'admin']
    }
  },
  {
    path: '/dashboard',
    name: 'dashboard',
    component: () => import('../views/DashboardView.vue'),
    meta: {
      requiresAuth: true,
      requiresRole: ['teacher', 'admin']
    }
  },
  {
    path: '/teacher/courses',
    redirect: '/courses'
  },
  {
    path: '/course/:id',
    redirect: to => `/courses/${to.params.id}`
  },
  {
    path: '/courses/:id',
    name: 'course-detail',
    component: () => import('../views/CourseDetailView.vue'),
    meta: {
      requiresAuth: true,
      requiresPermission: PERMISSIONS.VIEW_COURSES
    }
  },
  {
    path: '/courses/:id/study',
    name: 'course-study',
    component: () => import('../views/CourseStudyView.vue'),
    meta: {
      requiresAuth: true
    }
  },
  {
    path: '/exams/:id/take',
    name: 'exam-take',
    component: () => import('../views/ExamTakeView.vue'),
    meta: {
      requiresAuth: true,
      requiresPermission: PERMISSIONS.VIEW_EXAMS
    }
  },
  {
    path: '/exams/:id/result',
    name: 'exam-result',
    component: () => import('../views/ExamResultView.vue'),
    meta: {
      requiresAuth: true,
      requiresPermission: PERMISSIONS.VIEW_EXAMS
    }
  },
  {
    path: '/certificates',
    name: 'certificates',
    component: () => import('../views/CertificatesView.vue'),
    meta: { requiresAuth: true }
  },
  {
    path: '/certificates/:id',
    name: 'certificate-detail',
    component: () => import('../views/CertificateDetailView.vue'),
    meta: { requiresAuth: true }
  },
  {
    path: '/meetings',
    name: 'meetings',
    component: () => import('../views/MeetingsView.vue'),
    meta: {
      requiresAuth: true,
      requiresPermission: PERMISSIONS.VIEW_MEETINGS
    }
  },
  {
    path: '/meeting-management',
    name: 'meeting-management',
    component: () => import('../views/MeetingManagementView.vue'),
    meta: {
      requiresAuth: true,
      requiresRole: ['teacher', 'admin']
    }
  }
]

const router = createRouter({
  history: createWebHistory(process.env.BASE_URL),
  routes
})

// 全局前置守卫
router.beforeEach((to, from, next) => {
  // 简单的认证检查，不依赖Pinia store
  const hasToken = !!getAccessToken()
  const userInfo = getUserInfo()
  const isAuthenticated = hasToken && userInfo
  
  // 检查是否需要认证
  if (to.meta.requiresAuth && !isAuthenticated) {
    return next({ name: 'login' })
  }
  
  // 已认证用户访问登录/注册页，重定向到首页（密码超期解锁页除外）
  if (isAuthenticated && (to.name === 'login' || to.name === 'register')) {
    return next({ name: 'home' })
  }
  
  // 角色检查
  if (to.meta.requiresRole && isAuthenticated) {
    const userRole = userInfo?.role
    const requiredRoles = Array.isArray(to.meta.requiresRole) 
      ? to.meta.requiresRole 
      : [to.meta.requiresRole]
    
    if (!requiredRoles.includes(userRole)) {
      console.warn(`用户角色 ${userRole} 无权访问 ${to.path}`)
      return next({ name: 'home' })
    }
  }
  
  // 权限检查
  if (to.meta.requiresPermission && isAuthenticated) {
    const requiredPermission = to.meta.requiresPermission
    try {
      // 直接从用户信息中获取权限，不依赖 store
      let userPermissions = []
      
      // 收集所有可能的权限来源
      // 1. 后端返回的 permissions 字段
      if (userInfo.permissions && Array.isArray(userInfo.permissions) && userInfo.permissions.length > 0) {
        userPermissions = [...userInfo.permissions]
      }
      // 2. 自定义权限
      if (userInfo.custom_permissions && Array.isArray(userInfo.custom_permissions) && userInfo.custom_permissions.length > 0) {
        userPermissions = [...new Set([...userPermissions, ...userInfo.custom_permissions])]
      }
      // 3. 角色默认权限（合并，确保即使后端权限不完整也能使用角色默认权限）
      if (userInfo.role && ROLE_PERMISSIONS[userInfo.role]) {
        userPermissions = [...new Set([...userPermissions, ...ROLE_PERMISSIONS[userInfo.role]])]
      }
      
      console.log(`[路由守卫] 检查权限: ${requiredPermission}, 用户权限:`, userPermissions, '用户角色:', userInfo.role)
      
      const hasPermission = userPermissions.includes(requiredPermission)
      if (!hasPermission) {
        console.warn(`用户缺少权限 ${requiredPermission}，无法访问 ${to.path}`)
        // 如果用户已登录但缺少权限，重定向到首页而不是登录页
        return next({ name: 'home' })
      }
      
      console.log(`[路由守卫] 权限检查通过，允许访问 ${to.path}`)
    } catch (error) {
      console.error('权限检查出错:', error)
      // 权限检查出错时，也重定向到首页
      return next({ name: 'home' })
    }
  }
  
  next()
})

export default router